import React, { useState, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Slider } from '../../components/ui/slider';
import {
  QrCode,
  Download,
  Copy,
  Palette,
  Settings,
  Image,
  Link2,
  Phone,
  Mail,
  Wifi,
  CreditCard,
  MapPin,
  Calendar
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';

const QRCodePage = () => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const qrRef = useRef(null);

  const [qrType, setQrType] = useState('url');
  const [qrData, setQrData] = useState({
    url: 'https://www.monsite.com',
    text: '',
    phone: '',
    email: '',
    subject: '',
    body: '',
    wifi_ssid: '',
    wifi_password: '',
    wifi_type: 'WPA'
  });

  const [qrStyle, setQrStyle] = useState({
    size: 256,
    fgColor: '#1a365d',
    bgColor: '#ffffff',
    includeMargin: true,
    logoUrl: ''
  });

  const [generatedHistory, setGeneratedHistory] = useState([
    { id: 1, type: 'url', data: 'https://msc.link/promo', createdAt: '2024-12-20', name: 'Promo Noël' },
    { id: 2, type: 'wifi', data: 'Bureau MSC', createdAt: '2024-12-15', name: 'WiFi Bureau' },
    { id: 3, type: 'email', data: 'contact@msc.fr', createdAt: '2024-12-10', name: 'Contact rapide' },
  ]);

  const qrTypes = [
    { id: 'url', label: 'URL / Lien', icon: Link2 },
    { id: 'phone', label: 'Téléphone', icon: Phone },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'wifi', label: 'WiFi', icon: Wifi },
    { id: 'text', label: 'Texte libre', icon: QrCode },
  ];

  const generateQRValue = () => {
    switch (qrType) {
      case 'url':
        return qrData.url;
      case 'phone':
        return `tel:${qrData.phone}`;
      case 'email':
        return `mailto:${qrData.email}?subject=${encodeURIComponent(qrData.subject)}&body=${encodeURIComponent(qrData.body)}`;
      case 'wifi':
        return `WIFI:T:${qrData.wifi_type};S:${qrData.wifi_ssid};P:${qrData.wifi_password};;`;
      case 'text':
        return qrData.text;
      default:
        return '';
    }
  };

  const handleDownload = (format) => {
    // In production, this would generate actual QR code image
    toast.success(`QR Code téléchargé en ${format.toUpperCase()}`);
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(generateQRValue());
    toast.success('Données copiées !');
  };

  const renderQRForm = () => {
    switch (qrType) {
      case 'url':
        return (
          <div className="space-y-2">
            <Label>URL</Label>
            <Input
              value={qrData.url}
              onChange={(e) => setQrData({ ...qrData, url: e.target.value })}
              placeholder="https://www.exemple.com"
              className="input-default"
            />
          </div>
        );
      case 'phone':
        return (
          <div className="space-y-2">
            <Label>Numéro de téléphone</Label>
            <Input
              value={qrData.phone}
              onChange={(e) => setQrData({ ...qrData, phone: e.target.value })}
              placeholder="+33 6 12 34 56 78"
              className="input-default"
            />
          </div>
        );
      case 'email':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Adresse email</Label>
              <Input
                value={qrData.email}
                onChange={(e) => setQrData({ ...qrData, email: e.target.value })}
                placeholder="contact@exemple.com"
                className="input-default"
              />
            </div>
            <div className="space-y-2">
              <Label>Sujet (optionnel)</Label>
              <Input
                value={qrData.subject}
                onChange={(e) => setQrData({ ...qrData, subject: e.target.value })}
                placeholder="Demande d'information"
                className="input-default"
              />
            </div>
            <div className="space-y-2">
              <Label>Message (optionnel)</Label>
              <Input
                value={qrData.body}
                onChange={(e) => setQrData({ ...qrData, body: e.target.value })}
                placeholder="Bonjour..."
                className="input-default"
              />
            </div>
          </div>
        );
      case 'wifi':
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nom du réseau (SSID)</Label>
              <Input
                value={qrData.wifi_ssid}
                onChange={(e) => setQrData({ ...qrData, wifi_ssid: e.target.value })}
                placeholder="MonWiFi"
                className="input-default"
              />
            </div>
            <div className="space-y-2">
              <Label>Mot de passe</Label>
              <Input
                type="password"
                value={qrData.wifi_password}
                onChange={(e) => setQrData({ ...qrData, wifi_password: e.target.value })}
                placeholder="••••••••"
                className="input-default"
              />
            </div>
            <div className="space-y-2">
              <Label>Type de sécurité</Label>
              <select
                value={qrData.wifi_type}
                onChange={(e) => setQrData({ ...qrData, wifi_type: e.target.value })}
                className="w-full px-3 py-2 border rounded-xl"
              >
                <option value="WPA">WPA/WPA2</option>
                <option value="WEP">WEP</option>
                <option value="nopass">Aucun</option>
              </select>
            </div>
          </div>
        );
      case 'text':
        return (
          <div className="space-y-2">
            <Label>Texte</Label>
            <textarea
              value={qrData.text}
              onChange={(e) => setQrData({ ...qrData, text: e.target.value })}
              placeholder="Entrez votre texte..."
              className="w-full px-3 py-2 border rounded-xl min-h-[100px] input-default"
            />
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar
        collapsed={collapsed}
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />

      <main className={cn(
        "transition-all duration-300 p-6 md:p-8 pt-24",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}>
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold" style={{ color: theme.colors?.primary }}>
            Générateur de QR Code
          </h1>
          <p className="text-[#64748b]">
            Créez des QR codes personnalisés pour vos liens, contacts et plus
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Form */}
          <div className="space-y-6">
            {/* QR Type Selection */}
            <Card className="card">
              <CardHeader>
                <CardTitle>Type de QR Code</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                  {qrTypes.map((type) => {
                    const Icon = type.icon;
                    return (
                      <button
                        key={type.id}
                        onClick={() => setQrType(type.id)}
                        className={cn(
                          "flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all",
                          qrType === type.id
                            ? "border-[var(--color-accent)] bg-[var(--color-accent)]/10"
                            : "border-[#e2e8f0] hover:border-[#cbd5e1]"
                        )}
                      >
                        <Icon className={cn(
                          "w-5 h-5",
                          qrType === type.id ? "text-[var(--color-accent)]" : "text-[#64748b]"
                        )} />
                        <span className={cn(
                          "text-xs font-medium",
                          qrType === type.id ? "text-[var(--color-accent)]" : "text-[#64748b]"
                        )}>
                          {type.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Data Form */}
            <Card className="card">
              <CardHeader>
                <CardTitle>Données</CardTitle>
              </CardHeader>
              <CardContent>
                {renderQRForm()}
              </CardContent>
            </Card>

            {/* Style Options */}
            <Card className="card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="w-5 h-5" />
                  Personnalisation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <Label>Taille: {qrStyle.size}px</Label>
                  <Slider
                    value={[qrStyle.size]}
                    onValueChange={([v]) => setQrStyle({ ...qrStyle, size: v })}
                    min={128}
                    max={512}
                    step={32}
                    className="w-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Couleur du QR</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={qrStyle.fgColor}
                        onChange={(e) => setQrStyle({ ...qrStyle, fgColor: e.target.value })}
                        className="w-10 h-10 rounded-lg border cursor-pointer"
                      />
                      <Input
                        value={qrStyle.fgColor}
                        onChange={(e) => setQrStyle({ ...qrStyle, fgColor: e.target.value })}
                        className="input-default flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Couleur de fond</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="color"
                        value={qrStyle.bgColor}
                        onChange={(e) => setQrStyle({ ...qrStyle, bgColor: e.target.value })}
                        className="w-10 h-10 rounded-lg border cursor-pointer"
                      />
                      <Input
                        value={qrStyle.bgColor}
                        onChange={(e) => setQrStyle({ ...qrStyle, bgColor: e.target.value })}
                        className="input-default flex-1"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Preview */}
          <div className="space-y-6">
            <Card className="card">
              <CardHeader>
                <CardTitle>Aperçu</CardTitle>
              </CardHeader>
              <CardContent>
                <div 
                  ref={qrRef}
                  className="flex items-center justify-center p-8 rounded-xl"
                  style={{ backgroundColor: qrStyle.bgColor }}
                >
                  {/* Placeholder QR Code - In production, use a QR library */}
                  <div 
                    className="relative"
                    style={{ width: qrStyle.size, height: qrStyle.size }}
                  >
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      {/* Simplified QR pattern */}
                      <rect x="0" y="0" width="100" height="100" fill={qrStyle.bgColor} />
                      <g fill={qrStyle.fgColor}>
                        {/* Position patterns */}
                        <rect x="5" y="5" width="25" height="25" />
                        <rect x="70" y="5" width="25" height="25" />
                        <rect x="5" y="70" width="25" height="25" />
                        <rect x="10" y="10" width="15" height="15" fill={qrStyle.bgColor} />
                        <rect x="75" y="10" width="15" height="15" fill={qrStyle.bgColor} />
                        <rect x="10" y="75" width="15" height="15" fill={qrStyle.bgColor} />
                        <rect x="15" y="15" width="5" height="5" fill={qrStyle.fgColor} />
                        <rect x="80" y="15" width="5" height="5" fill={qrStyle.fgColor} />
                        <rect x="15" y="80" width="5" height="5" fill={qrStyle.fgColor} />
                        {/* Data pattern (simplified) */}
                        <rect x="35" y="5" width="5" height="5" />
                        <rect x="45" y="5" width="5" height="5" />
                        <rect x="55" y="5" width="5" height="5" />
                        <rect x="35" y="15" width="5" height="5" />
                        <rect x="50" y="15" width="5" height="5" />
                        <rect x="35" y="35" width="5" height="5" />
                        <rect x="45" y="45" width="10" height="10" />
                        <rect x="60" y="35" width="5" height="5" />
                        <rect x="70" y="45" width="5" height="5" />
                        <rect x="85" y="55" width="5" height="5" />
                        <rect x="35" y="60" width="5" height="5" />
                        <rect x="50" y="70" width="5" height="5" />
                        <rect x="60" y="80" width="5" height="5" />
                        <rect x="70" y="70" width="5" height="5" />
                        <rect x="80" y="85" width="5" height="5" />
                      </g>
                    </svg>
                  </div>
                </div>
                
                {/* Download buttons */}
                <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
                  <Button onClick={() => handleDownload('png')} variant="outline" className="flex-1 rounded-full">
                    <Download className="w-4 h-4 mr-2" />
                    PNG
                  </Button>
                  <Button onClick={() => handleDownload('svg')} variant="outline" className="flex-1 rounded-full">
                    <Download className="w-4 h-4 mr-2" />
                    SVG
                  </Button>
                  <Button onClick={() => handleDownload('pdf')} variant="outline" className="flex-1 rounded-full">
                    <Download className="w-4 h-4 mr-2" />
                    PDF
                  </Button>
                  <Button onClick={handleCopyToClipboard} variant="outline" className="rounded-full">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* History */}
            <Card className="card">
              <CardHeader>
                <CardTitle>Historique récent</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {generatedHistory.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-3 rounded-xl bg-[#f8fafc]">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center"
                          style={{ backgroundColor: `${theme.colors?.accent}20` }}
                        >
                          <QrCode className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                        </div>
                        <div>
                          <p className="font-medium text-sm" style={{ color: theme.colors?.primary }}>{item.name}</p>
                          <p className="text-xs text-[#64748b]">{item.type} • {item.createdAt}</p>
                        </div>
                      </div>
                      <Button variant="ghost" size="icon" className="rounded-full">
                        <Download className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default QRCodePage;
